import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AddMakeModal from './AddMakeModal';
import { CATEGORIES_BY_USAGE, getCategoryLabel } from '../../constants/categories';
import { IconPlus, IconBuildingFactory2 } from '@tabler/icons-react';

export default function Library() {
  const navigate = useNavigate();
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'makes'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setMakes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = makes.filter(m => {
    if (search && !m.name?.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'all') return true;
    return m.supportedTypes?.includes(filter);
  });

  return (
    <Layout>
      <PageHeader
        title="Library — Makes"
        subtitle={`${makes.length} make${makes.length !== 1 ? 's' : ''} in the library`}
        actions={
          <Button icon={<IconPlus size={14} stroke={2} />} onClick={() => setShowAddModal(true)}>
            New make
          </Button>
        }
      />

      <div style={{ padding: 28 }}>
        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <Chip label="All" active={filter === 'all'} onClick={() => setFilter('all')} count={makes.length} />
          {CATEGORIES_BY_USAGE.map(cat => (
            <Chip
              key={cat.code}
              label={cat.label}
              active={filter === cat.code}
              onClick={() => setFilter(cat.code)}
              count={makes.filter(m => m.supportedTypes?.includes(cat.code)).length}
            />
          ))}
        </div>

        {/* Search */}
        <div style={{ marginBottom: 20 }}>
          <input
            type="text"
            placeholder="Search makes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>

        {/* Makes grid */}
        {loading ? (
          <div style={{ color: 'var(--color-text-tertiary)', fontSize: 13 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <Card>
            <div style={{ padding: '40px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                {makes.length === 0 ? 'No makes in the library yet' : 'No makes match your filter'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                {makes.length === 0 ? 'Add the first make to get started.' : 'Try adjusting the filter or search.'}
              </div>
            </div>
          </Card>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 16,
          }}>
            {filtered.map(make => (
              <MakeCard key={make.id} make={make} onClick={() => navigate(`/library/makes/${make.id}`)} />
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <AddMakeModal
          existingMakes={makes}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </Layout>
  );
}

function Chip({ label, active, onClick, count }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 'var(--radius-full)',
        background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)',
        color: active ? '#fff' : 'var(--color-text-secondary)',
        border: active ? 'none' : '0.5px solid var(--color-border-light)',
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'all 0.15s',
      }}
    >
      {label}
      <span style={{
        fontSize: 10,
        opacity: active ? 0.85 : 0.5,
      }}>{count}</span>
    </div>
  );
}

function MakeCard({ make, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        aspectRatio: '1',
        background: 'var(--color-bg-primary)',
        border: '0.5px solid var(--color-border-light)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-border-medium)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-light)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Logo or fallback */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
        background: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}>
        {make.logoUrl ? (
          <img src={make.logoUrl} alt={make.name} style={{ maxWidth: '70%', maxHeight: '70%', objectFit: 'contain' }} />
        ) : (
          <IconBuildingFactory2 size={36} stroke={1} color="var(--color-text-tertiary)" />
        )}
      </div>

      {/* Name + meta */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {make.name}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {(make.supportedTypes || []).slice(0, 3).map(code => (
            <span key={code} style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              {getCategoryLabel(code)}
            </span>
          ))}
          {make.status === 'pending_review' && (
            <span style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-warning-light)',
              color: 'var(--color-warning)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}>
              Review
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
